<template>
    <div>
        <div class="demo-spin-article">
            <div class="row mb-3">
                <div class="col-md-6">
                    <label class="form-col-label control-label required">{{
                        $t("motorRisk.reportDate")
                    }}</label>
                    <DatePicker
                        :value="model.report_date"
                        placeholder="DD-MM-YYYY"
                        format="dd-MM-yyyy"
                        @on-change="onChangeReportDate"
                        :options="options"
                        style="width: 100%"
                    ></DatePicker>
                    <div class="text-danger" v-if="errors.has('report_date')">
                        {{ errors.first("report_date") }}
                    </div>
                </div>
                <div class="col-md-6">
                    <label class="form-col-label control-label required">{{
                        $t("motorRisk.customer")
                    }}</label>
                    <Select
                        v-model="model.customer_id"
                        filterable
                        clearable
                        :placeholder="$t('motorRisk.customer')"
                        :class="{
                            'ivu-form-item-error': errors.has('customer_id')
                        }"
                    >
                        <Option
                            v-for="(option, index) in customers"
                            :value="option.customer_id"
                            :key="index"
                        >
                            {{ option.customer_name_en }} |
                            {{ option.customer_name_kh }}
                        </Option>
                    </Select>
                    <div class="text-danger" v-if="errors.has('customer_id')">
                        {{ errors.first("customer_id") }}
                    </div>
                </div>
            </div>
            <div class="row mb-3">
                <div class="col-md-4">
                    <label class="form-col-label control-label">{{
                        $t("motorRisk.motorType")
                    }}</label>
                    <input
                        v-model.trim="model.motor_type"
                        type="text"
                        class="form-control"
                        :class="{ 'is-invalid': errors.has('motor_type') }"
                    />
                    <div
                        class="invalid-feedback"
                        v-if="errors.has('motor_type')"
                    >
                        {{ errors.first("motor_type") }}
                    </div>
                </div>
                <div class="col-md-4">
                    <label class="form-col-label control-label required">{{
                        $t("motorRisk.outstandingAmount")
                    }}</label>
                    <div class="tw-flex tw-space-x-2">
                        <InputNumber
                            v-model="model.outstanding_amount"
                            :min="0"
                            style="width: 60%"
                        ></InputNumber>
                        <Select
                            v-model="model.currency_id"
                            style="width: 40%"
                        >
                            <Option
                                v-for="(option, index) in currencies"
                                :value="option.currency_id"
                                :key="index"
                            >
                                {{ option.sign }} {{ option.currency_code }}
                            </Option>
                        </Select>
                    </div>
                </div>
                <div class="col-md-4">
                    <label class="form-col-label control-label">{{
                        $t("motorRisk.riskStatus")
                    }}</label>
                    <Select
                        v-model="model.risk_status_id"
                        clearable
                        :placeholder="$t('motorRisk.riskStatus')"
                        :class="{
                            'ivu-form-item-error': errors.has('risk_status_id')
                        }"
                    >
                        <Option
                            v-for="(option, index) in riskStatus"
                            :value="option.risk_status_id"
                            :key="index"
                        >
                            {{ option.risk_status_name }}
                        </Option>
                    </Select>
                    <div
                        class="text-danger"
                        v-if="errors.has('risk_status_id')"
                    >
                        {{ errors.first("risk_status_id") }}
                    </div>
                </div>
            </div>
            <div class="mb-3">
                <div class="col-md-12">
                    <label class="form-col-label control-label required">{{
                        $t("motorRisk.reason")
                    }}</label>
                    <textarea
                        v-model="model.reason"
                        class="form-control"
                        rows="3"
                        :class="{ 'is-invalid': errors.has('reason') }"
                    ></textarea>
                    <div class="invalid-feedback" v-if="errors.has('reason')">
                        {{ errors.first("reason") }}
                    </div>
                </div>
            </div>
            <div class="mb-3">
                <div class="col-md-12">
                    <label class="form-col-label control-label required">{{
                        $t("motorRisk.branchSolution")
                    }}</label>
                    <textarea
                        v-model="model.branch_solution"
                        class="form-control"
                        rows="3"
                        :class="{ 'is-invalid': errors.has('branch_solution') }"
                    ></textarea>
                    <div class="invalid-feedback" v-if="errors.has('branch_solution')">
                        {{ errors.first("branch_solution") }}
                    </div>
                </div>
            </div>
            <div slot="footer">
                <div class="tw-flex tw-justify-end tw-space-x-2 tw-sh">
                    <ts-button
                        @click.prevent="() => $emit('cancel')"
                        class="btn-gray"
                    >
                        {{ $t("cancel") }}</ts-button
                    >
                    <ts-button
                        color="primary"
                        v-if="!isUpdate"
                        outline
                        :waiting="waiting_new"
                        :disabled="waiting"
                        @click.prevent="onSaveAddNew"
                    >
                        {{ $t("saveAddNew") }}</ts-button
                    >
                    <ts-button
                        color="primary"
                        v-if="!isUpdate"
                        :waiting="waiting"
                        :disabled="waiting_new"
                        @click.prevent="onSave"
                    >
                        {{ $t("save") }}</ts-button
                    >
                    <ts-button
                        v-if="isUpdate"
                        color="primary"
                        @click.prevent="onUpdate"
                        :waiting="waiting"
                    >
                        {{ $t("update") }}</ts-button
                    >
                </div>
            </div>
        </div>
        <Spin size="large" fix v-if="loading"> </Spin>
    </div>
</template>

<script>
import { Errors } from "form-backend-validation";
import { mapState } from "vuex";
import { isEmpty } from "lodash";
import moment from "moment";

export default {
    name: "motorRiskForm",
    data() {
        return {
            errors: new Errors(),
            waiting: false,
            waiting_new: false,
            loading: false,
            model: {
                report_date: moment().format("DD-MM-YYYY"),
                customer_id: null,
                motor_type: null,
                outstanding_amount: null,
                risk_status_id: null,
                reason: null,
                branch_solution: null,
            },
            options: {
                disabledDate(date) {
                    return date.valueOf() > Date.now();
                }
            }
        };
    },
    computed: {
        ...mapState("creditOperation/motorRisk", [
            "edit_data",
            "customers",
            "riskStatus",
            "currencies"
        ]),
        isUpdate() {
            return !isEmpty(this.edit_data);
        }
    },
    methods: {
        onChangeReportDate(date) {
            this.model.report_date = date;
        },
        async fetchResource() {
            this.loading = true;
            await this.$store.dispatch(
                "creditOperation/motorRisk/getCustomers"
            );
            await this.$store.dispatch(
                "creditOperation/motorRisk/getRiskStatus"
            );
            await this.$store.dispatch(
                "creditOperation/motorRisk/getCurrencies"
            );
            this.setEditData();
            this.loading = false;
        },
        onSaveAddNew() {
            this.errors = new Errors();
            this.waiting_new = true;
            this.$store
                .dispatch("creditOperation/motorRisk/store", this.model)
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.clearInput();
                    this.$emit("fetchData");
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => {
                    this.waiting_new = false;
                });
        },
        onSave() {
            this.errors = new Errors();
            this.waiting = true;
            this.$store
                .dispatch("creditOperation/motorRisk/store", this.model)
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.$emit("cancel");
                    this.$emit("fetchData");
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => {
                    this.waiting = false;
                });
        },
        onUpdate() {
            this.errors = new Errors();
            this.waiting = true;
            this.$store
                .dispatch("creditOperation/motorRisk/update", {
                    id: this.edit_data.risk_id,
                    data: this.model
                })
                .then(response => {
                    this.notice({
                        type: response.message_id == 0 ? "success" : "warning",
                        text: response.message
                    });
                    this.$emit("cancel");
                    this.$emit("fetchData");
                })
                .catch(error => {
                    this.errors = new Errors(error.errors);
                    this.notice({ type: "error", text: error.message });
                })
                .finally(() => {
                    this.waiting = false;
                });
        },
        setEditData() {
            this.errors = new Errors();
            if (!isEmpty(this.edit_data)) {
                this.model.report_date = this.edit_data.report_date;
                this.model.customer_id = this.edit_data.customer_id;
                this.model.motor_type = this.edit_data.motor_type;
                this.model.outstanding_amount = this.edit_data.outstanding_amount;
                this.model.risk_status_id = this.edit_data.risk_status_id;
                this.model.reason = this.edit_data.reason;
                this.model.branch_solution = this.edit_data.branch_solution;
            }
        },
        clearInput() {
            this.model.report_date = moment().format("DD-MM-YYYY");
            this.model.customer_id = null;
            this.model.motor_type = null;
            this.model.outstanding_amount = null;
            this.model.risk_status_id = null;
            this.model.reason = null;
            this.model.branch_solution = null;
        },
        notice(not) {
            this.$Notice[not.type]({
                title: "MOTOR RISK",
                desc: not.text
            });
        }
    }
};
</script>
